import React, { useMemo } from 'react'

import {
  GCP_INTEGRATION_SETUP_MESSAGES,
  isTeamExternalIntegrationGcp
} from 'components/common/mapped-entities/gcp/authentication/gcp-integration-utils'
import { GcpNewAuthenticationForm } from 'components/common/mapped-entities/gcp/authentication/GcpNewAuthenticationForm'
import PageSection from 'components/common/PageSection'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useGetGcpIntegrationSetup } from 'queries/gcp/use-get-gcp-integration-setup'
import { messageFromResponseError } from 'utils/strings'
import { type OnSuccessExternalIntegration } from 'utils/team-external-integrations/team-external-integrations-util'

export function GcpNewAuthenticationContainer({
  isEnabled,
  onSuccess
}: {
  isEnabled: boolean
  onSuccess?: OnSuccessExternalIntegration
}) {
  const {
    externalIntegrationsError,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    teamExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const {
    data: gcpIntegrationSetupData,
    error: errorGcpMetadataAuth,
    isError: isErrorGcpMetadataAuth,
    isLoading: isLoadingGcpMetadataAuth
  } = useGetGcpIntegrationSetup({
    enabled: isEnabled
  })

  const gcpExternalIntegrations = useMemo(() => {
    if (!isEnabled || !teamExternalIntegrations) {
      return []
    }

    return teamExternalIntegrations.filter(isTeamExternalIntegrationGcp)
  }, [isEnabled, teamExternalIntegrations])

  const isLoading = isFetchingExternalIntegrations || isLoadingGcpMetadataAuth
  const hasError = hasErrorExternalIntegrations || isErrorGcpMetadataAuth
  const errorMessage = hasErrorExternalIntegrations
    ? messageFromResponseError(
        externalIntegrationsError,
        'There was an issue while retrieving External Integrations'
      )
    : messageFromResponseError(
        errorGcpMetadataAuth,
        GCP_INTEGRATION_SETUP_MESSAGES.errorFallback
      )

  return (
    <PageSection
      errorMessage={errorMessage}
      hasError={hasError}
      isLoading={isLoading}
      isLoadingOverlayFullScreen={false}
    >
      {() => {
        if (!gcpIntegrationSetupData) {
          throw new Error(GCP_INTEGRATION_SETUP_MESSAGES.unexpectedResponse)
        }

        return (
          <GcpNewAuthenticationForm
            gcpExternalIntegrations={gcpExternalIntegrations}
            gcpIntegrationSetupData={gcpIntegrationSetupData}
            onSuccess={onSuccess}
          />
        )
      }}
    </PageSection>
  )
}
